import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { api } from "../App";
import { toast } from "sonner";
import Header from "../components/shared/Header";
import Footer from "../components/shared/Footer";
import SEOHead from "../components/shared/SEOHead";
import Breadcrumbs from "../components/shared/Breadcrumbs";
import { Button } from "../components/ui/button";
import { StepWhenWhere } from "../components/quote/StepWhenWhere";
import { StepPropertyDetails } from "../components/quote/StepPropertyDetails";
import { StepInventory } from "../components/quote/StepInventory";
import { StepServices } from "../components/quote/StepServices";
import { StepContact } from "../components/quote/StepContact";
import { QuoteResult } from "../components/quote/QuoteResult";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";

const TOTAL_STEPS = 5;
const PROGRESS_ANIMATION = { duration: 0.3 };

const InstantQuotePage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null);

  const [formData, setFormData] = useState({
    move_date: null,
    from_postcode: "",
    to_postcode: "",
    property_type: "house",
    bedrooms: 2,
    floor_level: 0,
    lift_available: false,
    parking_restrictions: false,
    inventory_size: "medium",
    large_items: [],
    specialist_items: [],
    packing_required: false,
    dismantling_required: false,
    storage_required: false,
    storage_weeks: 1,
    name: "",
    email: "",
    phone: "",
    notes: ""
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, item) => {
    setFormData(prev => {
      const arr = prev[field] || [];
      if (arr.includes(item)) {
        return { ...prev, [field]: arr.filter(i => i !== item) };
      }
      return { ...prev, [field]: [...arr, item] };
    });
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.move_date && formData.from_postcode && formData.to_postcode;
      case 2:
        return formData.property_type && formData.bedrooms !== undefined;
      case 3:
        return formData.inventory_size;
      case 4:
        return true;
      case 5:
        return formData.name && formData.email && formData.phone;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        move_date: formData.move_date ? format(formData.move_date, "yyyy-MM-dd") : null,
        bedrooms: parseInt(formData.bedrooms),
      };
      const response = await api.post("/quotes", payload);
      setQuoteResult(response.data);
      setStep(6);
      sessionStorage.setItem("quote_completed", "true");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create quote");
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToPayment = async () => {
    if (quoteResult?.booking_id) {
      navigate(`/payment/${quoteResult.booking_id}`);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return <StepWhenWhere formData={formData} updateFormData={updateFormData} />;
      case 2:
        return <StepPropertyDetails formData={formData} updateFormData={updateFormData} />;
      case 3:
        return <StepInventory formData={formData} updateFormData={updateFormData} toggleArrayItem={toggleArrayItem} />;
      case 4:
        return <StepServices formData={formData} updateFormData={updateFormData} toggleArrayItem={toggleArrayItem} />;
      case 5:
        return <StepContact formData={formData} updateFormData={updateFormData} />;
      case 6:
        return <QuoteResult quoteResult={quoteResult} loading={loading} onProceedToPayment={handleProceedToPayment} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Instant Removal Quote | Free Online Calculator | Hitech Transit"
        description="Get an instant removal quote in 60 seconds. Free, no-obligation estimates for house moves, office relocations and more. Hitech Transit, Cardiff."
        canonical="/instant-quote"
      />
      <Header />
      
      <main className="pt-20">
        <div className="container-max px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-xl mx-auto">
            {/* Progress Bar */}
            {step <= 5 && (
              <div className="mb-8">
                <div className="flex items-center justify-between text-sm text-slate-500 mb-2">
                  <span>Step {step} of {TOTAL_STEPS}</span>
                  <span>{Math.round((step / TOTAL_STEPS) * 100)}% complete</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-[#FF5A1F]"
                    initial={{ width: 0 }}
                    animate={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
                    transition={PROGRESS_ANIMATION}
                  />
                </div>
              </div>
            )}

            {/* Form Steps */}
            <div className="bg-white border border-slate-200 p-8">
              <AnimatePresence mode="wait">
                {renderStep()}
              </AnimatePresence>

              {/* Navigation */}
              {step <= 5 && (
                <div className="flex gap-4 mt-8 pt-6 border-t border-slate-200">
                  {step > 1 && (
                    <Button
                      variant="outline"
                      onClick={() => setStep(step - 1)}
                      className="flex-1 h-12"
                      data-testid="back-btn"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                  )}
                  
                  {step < 5 ? (
                    <Button
                      onClick={() => setStep(step + 1)}
                      disabled={!canProceed()}
                      className="flex-1 btn-primary h-12"
                      data-testid="next-btn"
                    >
                      Next
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      disabled={!canProceed() || loading}
                      className="flex-1 btn-primary h-12"
                      data-testid="get-quote-btn"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Get My Quote"}
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Trust indicators */}
            {step <= 5 && (
              <div className="mt-6 flex justify-center gap-6 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> No obligation
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> Instant response
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-green-500" /> Free survey
                </span>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default InstantQuotePage;
